<template>
  <teleport to="body">
    <transition name="overlay">
      <div
        v-if="open"
        class="overlay-root"
        :class="{ 'is-sheet-mobile': mobileSheet }"
        @mousedown.self="onBackdrop"
      >
        <section
          ref="dialog"
          class="overlay-panel"
          :class="[`size-${size}`, panelClass]"
          :role="role"
          aria-modal="true"
          :aria-labelledby="titleId || undefined"
          :aria-label="ariaLabel || undefined"
          tabindex="-1"
          @keydown="onKeydown"
        >
          <slot />
        </section>
      </div>
    </transition>
  </teleport>
</template>

<script setup>
import { nextTick, onBeforeUnmount, ref, watch } from "vue";

const props = defineProps({
  open: { type: Boolean, default: false },
  size: {
    type: String,
    default: "m",
    validator: (value) => ["xs", "s", "m", "l", "xl"].includes(value),
  },
  role: { type: String, default: "dialog" },
  titleId: { type: String, default: "" },
  ariaLabel: { type: String, default: "" },
  panelClass: { type: [String, Array, Object], default: "" },
  closeOnBackdrop: { type: Boolean, default: true },
  closeOnEscape: { type: Boolean, default: true },
  persistent: { type: Boolean, default: false },
  mobileSheet: { type: Boolean, default: false },
});

const emit = defineEmits(["close"]);
const dialog = ref(null);
let returnFocus = null;

watch(
  () => props.open,
  async (open) => {
    if (open) {
      returnFocus = document.activeElement;
      lockBody();
      await nextTick();
      focusFirst();
    } else {
      unlockBody();
      restoreFocus();
    }
  },
  { immediate: true },
);

onBeforeUnmount(() => {
  if (props.open) unlockBody();
  restoreFocus();
});

function onBackdrop() {
  if (props.closeOnBackdrop && !props.persistent) emit("close");
}

function onKeydown(event) {
  if (event.key === "Escape" && props.closeOnEscape && !props.persistent) {
    event.preventDefault();
    emit("close");
    return;
  }
  if (event.key !== "Tab") return;
  const items = focusableItems();
  if (!items.length) {
    event.preventDefault();
    dialog.value?.focus();
    return;
  }
  const first = items[0];
  const last = items[items.length - 1];
  if (event.shiftKey && document.activeElement === first) {
    event.preventDefault();
    last.focus();
  } else if (!event.shiftKey && document.activeElement === last) {
    event.preventDefault();
    first.focus();
  }
}

function focusFirst() {
  const preferred = dialog.value?.querySelector("[autofocus]");
  (preferred || focusableItems()[0] || dialog.value)?.focus?.({ preventScroll: true });
}

function focusableItems() {
  if (!dialog.value) return [];
  return Array.from(
    dialog.value.querySelectorAll(
      'button:not([disabled]),a[href],input:not([disabled]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])',
    ),
  ).filter((item) => !item.hasAttribute("hidden") && item.getClientRects().length);
}

function restoreFocus() {
  if (returnFocus?.isConnected) returnFocus.focus?.({ preventScroll: true });
  returnFocus = null;
}

function lockBody() {
  const count = Number(document.body.dataset.overlayCount || 0) + 1;
  document.body.dataset.overlayCount = String(count);
  document.body.classList.add("modal-open");
}

function unlockBody() {
  const count = Math.max(0, Number(document.body.dataset.overlayCount || 0) - 1);
  if (count) {
    document.body.dataset.overlayCount = String(count);
  } else {
    delete document.body.dataset.overlayCount;
    document.body.classList.remove("modal-open");
  }
}
</script>

<style scoped>
.overlay-root {
  position: fixed;
  inset: 0;
  z-index: 700;
  display: grid;
  place-items: center;
  padding: 20px;
  background: rgba(22, 18, 29, 0.56);
  backdrop-filter: blur(10px);
}
.overlay-panel {
  width: 100%;
  max-height: min(88dvh, 920px);
  overflow: auto;
  border: 1px solid rgba(39, 32, 55, 0.08);
  border-radius: 22px;
  outline: 0;
  background: #fff;
  color: var(--ui-text, #211e2b);
  box-shadow: 0 24px 72px rgba(31, 25, 38, 0.22);
}
.size-xs { max-width: 360px; }
.size-s { max-width: 440px; }
.size-m { max-width: 560px; }
.size-l { max-width: 720px; }
.size-xl { max-width: 1040px; }
.overlay-enter-active,
.overlay-leave-active {
  transition: opacity 0.16s ease;
}
.overlay-enter-active .overlay-panel,
.overlay-leave-active .overlay-panel {
  transition: transform 0.22s cubic-bezier(0.2, 0.8, 0.25, 1), opacity 0.18s ease;
}
.overlay-enter-from,
.overlay-leave-to {
  opacity: 0;
}
.overlay-enter-from .overlay-panel,
.overlay-leave-to .overlay-panel {
  opacity: 0;
  transform: translateY(12px) scale(0.98);
}
@media (max-width: 680px) {
  .overlay-root {
    padding: 16px;
    backdrop-filter: blur(6px);
  }
  .overlay-root.is-sheet-mobile {
    align-items: end;
    padding: 10px;
  }
  .is-sheet-mobile .overlay-panel {
    max-height: 88dvh;
    border-radius: 24px 24px 18px 18px;
  }
  .overlay-enter-from .overlay-panel,
  .overlay-leave-to .overlay-panel {
    transform: translateY(18px) scale(0.985);
  }
  .is-sheet-mobile.overlay-enter-from .overlay-panel,
  .is-sheet-mobile.overlay-leave-to .overlay-panel {
    transform: translateY(32px);
  }
}
@media (prefers-reduced-motion: reduce) {
  .overlay-enter-active,
  .overlay-leave-active,
  .overlay-enter-active .overlay-panel,
  .overlay-leave-active .overlay-panel {
    transition-duration: 0.01ms;
  }
}
</style>
